import { Button, Typography } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CommonModal from 'src/app/shared/components/modal/common-modal'
import { useTranslation } from 'react-i18next'
import Table from 'src/app/shared/components/common/table'
import { colorBorder } from 'src/app/themes/primaryThemeDefault'
import { getFileMappingClients } from 'src/app/shared/services/ClientSetupService'
import './index.scss'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'

interface Props {
  isOpen: boolean
  onClose: () => void
  fileMappingId: string
  fileMappingName?: string
}

const ViewClientsModal = (props: Props) => {
  const { isOpen, onClose, fileMappingName, fileMappingId } = props
  const { t } = useTranslation()
  const [data, setData] = useState<any>()
  const [loading, setIsLoading] = useState(false)
  const [suppressNoRowsOverlay, setSuppressNoRowsOverlay] = useState(false)

  const getClients = useCallback(async () => {
    try {
      setIsLoading(true)
      const res = await getFileMappingClients(fileMappingId)
      setData(res?.data)
      setSuppressNoRowsOverlay(res?.data?.length === 0)
      return res?.data
    } finally {
      setIsLoading(false)
    }
  }, [fileMappingId])

  useEffect(() => {
    if (fileMappingId) getClients()

    return () => {
      setData([])
      setSuppressNoRowsOverlay(false)
    }
  }, [fileMappingId, getClients])

  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`FileMappingSetup:view_clients.list.${field}`)
    },
    [t],
  )

  const columnDefs: Array<TableColumnDefs> = useMemo(() => {
    return [
      {
        colId: 'no',
        field: 'no',
        width: 80,
        headerName: t(getHeaderName('no')),
        cellRenderer: (params: any) => {
          return params.node.rowIndex + 1
        },
        sortable: false,
        resizable: false,
        hide: false,
        defaultSelect: true,
        headerClass: 'custom-col-resize-indicator',
        flex: undefined,
      },
      {
        colId: 'clientName',
        field: 'clientName',
        headerName: t(getHeaderName('clientName')),
        resizable: false,
        minWidth: 450,
        defaultSelect: true,
        sortIndex: 0,
        headerClass: 'custom-col-resize-indicator',
        flex: undefined,
      },
    ]
  }, [getHeaderName, t])

  return (
    <CommonModal
      title={t('FileMappingSetup:view_clients.clientListFor', { name: fileMappingName })}
      size='small'
      open={isOpen}
      onClose={onClose}
      footer={[
        <Button
          key='cancel-btn'
          style={{
            fontWeight: 600,
            width: '102px',
            height: '32px',
            border: `1px solid ${colorBorder}`,
          }}
          onClick={onClose}
        >
          <Typography.Text style={{ fontSize: 14 }}>{t('ClientSetup:client_setup.close')}</Typography.Text>
        </Button>,
      ]}
    >
      <div className='table-client-setup'>
        <Table
          columnDefs={columnDefs}
          rowData={data}
          displayAddBtn={false}
          displayUploadButton={false}
          handleChooseColumn={async () => {}}
          handleSearch={async () => {}}
          disableTableControl={true}
          displayActionColumn={false}
          autoSort={false}
          isGettingList={loading}
          suppressNoRowsOverlay={suppressNoRowsOverlay}
          getHeaderName={getHeaderName}
        />
      </div>
    </CommonModal>
  )
}

export default ViewClientsModal
